import React, { useEffect, useState } from "react";
import { ShieldAlert, X } from "lucide-react";
import { MedicallyCuratedCrest } from "./MedicallyCuratedCrest";

const STORAGE_KEY = "sp_media_gate_ack";

export const MediaAccessGate = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const ack = sessionStorage.getItem(STORAGE_KEY);
      if (!ack) setOpen(true);
    } catch {
      setOpen(true);
    }
  }, []);

  const acknowledge = () => {
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  // ESC key closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") acknowledge();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[hsl(var(--navy-deep))]/80 backdrop-blur-md"
      data-testid="media-access-gate"
      role="dialog"
      aria-modal="true"
      aria-labelledby="media-gate-title"
    >
      <div className="relative w-full max-w-lg rounded-2xl navy-card-bg border border-[hsl(var(--gold))]/40 shadow-2xl shadow-black/60 p-8 lg:p-10">
        <button
          type="button"
          onClick={acknowledge}
          aria-label="Close"
          data-testid="media-gate-close"
          className="absolute top-4 right-4 inline-flex items-center justify-center h-9 w-9 rounded-md text-white/60 hover:text-white hover:bg-white/10 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-4">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl gold-bg text-[hsl(var(--navy-deep))] shadow-sm">
            <ShieldAlert className="h-6 w-6" />
          </span>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] gold-text font-semibold">
              Private · Confidential
            </p>
            <h2
              id="media-gate-title"
              className="mt-1 font-serif text-2xl sm:text-3xl text-white leading-tight"
            >
              Research Access Only
            </h2>
          </div>
        </div>

        <div className="mt-7 space-y-4 text-sm text-white/75 leading-relaxed">
          <p>
            This page is intended for{" "}
            <span className="font-semibold text-white">
              partners, physicians, researchers, and approved collaborators
            </span>{" "}
            of SmartPeptides.ai.
          </p>
          <p>
            The clinical and product films hosted here are confidential
            pre-release materials. They are provided strictly for{" "}
            <span className="font-semibold text-white">
              research, education, and partnership-evaluation purposes
            </span>{" "}
            and may not be redistributed, re-uploaded, or reproduced without
            written authorization.
          </p>
          <p className="text-white/60">
            By continuing, you acknowledge that you are an authorized viewer and
            agree to handle these materials accordingly.
          </p>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={acknowledge}
            data-testid="media-gate-acknowledge"
            className="inline-flex items-center justify-center rounded-md gold-bg h-12 px-6 text-sm font-semibold text-[hsl(var(--navy-deep))] hover:opacity-90 transition-opacity flex-1"
          >
            I acknowledge — Continue
          </button>
          <a
            href="/"
            data-testid="media-gate-leave"
            className="inline-flex items-center justify-center rounded-md border border-white/20 h-12 px-6 text-sm font-medium text-white/80 hover:text-white hover:bg-white/5 transition-colors"
          >
            Return to site
          </a>
        </div>

        <div className="mt-7 pt-5 border-t border-white/10 flex items-center gap-3">
          <MedicallyCuratedCrest size={40} />
          <p className="text-[11px] text-white/45 leading-snug">
            SmartPeptides.ai · For research and education purposes only.
            Not medical advice.
          </p>
        </div>
      </div>
    </div>
  );
};
